import type { ReactNode } from 'react';
import type { OrderDetail } from '@skupreme/api-contracts/core';
import { useOrdersV2 } from '@features/orders/hooks/orders-v2';
import {
  ExternalLink,
  Badge,
  Card,
  CopyButton,
  FieldGrid,
  Tooltip,
} from '@skupreme/shared/ui';
import {
  MarketplaceAvatar,
  OrderAgeCell,
  OrderIssues,
  PartiallyCanceledBadge,
  SHORT_ID_LABEL,
  ShortIdCell,
  PastSlaBadge,
  StageBadge,
  StatusReasonBadge,
  HeldBadge,
  ProviderSendBadge,
  VIEW_STATE_LABELS,
  getPastSla,
  getProviderSend,
  getStage,
  getStatusReasons,
} from '@features/orders';
import {
  divisionLabelOf,
  hasDivision,
  useActiveScope,
} from '@features/account-view/active';
import { OrderStatus } from '@utility/constants/common';
import { CommonUtility } from '@utility/common';
import { DateUtility } from '@utility/date';
import { formatOrderStatus, getOrderStatusTone } from '../lib/status';
import { getSummaryCardIssues } from '../lib/order-errors';
import {
  formatTrackingStatus,
  getTrackingEntries,
  isKnownTrackingStatus,
  type OrderTrackingMap,
} from '../lib/tracking';

export type OrderSummaryCardProps = {
  order?: OrderDetail;
  tracking?: OrderTrackingMap;
  /** A clone inherits the source order's data but none of its dates or shipments. */
  isClone?: boolean;
  className?: string;
};

/** Not shipped yet: the order age keeps counting and a line can still ship. */
const PENDING_STATUSES: string[] = [
  OrderStatus.PendingShipment,
  OrderStatus.PendingFulfillment,
];

/** Renders nothing without a value, so a pending order shows no rows of dashes. */
function ValueField({ label, value }: { label: string; value?: ReactNode }) {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return (
    <div className="sk:flex sk:min-w-0 sk:items-baseline sk:gap-2">
      <span className="sk:w-32 sk:shrink-0 sk:text-dense sk:text-fg-tertiary">
        {label}
      </span>
      <div className="sk:min-w-0 sk:text-dense sk:font-medium sk:text-fg sk:tracking-dense">
        {value}
      </div>
    </div>
  );
}

/**
 * Carrier status and ETA arrive per shipment, so an order split across packages
 * has several of each; the card shows the one and defers the rest to a tooltip.
 */
function trackingValue(
  tracking: OrderTrackingMap | undefined,
  field: 'status' | 'eta',
): ReactNode {
  const all = getTrackingEntries(tracking).filter(entry => entry[field]);
  /* `UNKNOWN` is a placeholder our carrier mapping falls back to; printed as
     is it reads like a failure rather than "no scans yet". */
  const entries =
    field === 'status'
      ? all.filter(entry => isKnownTrackingStatus(entry.status))
      : all;

  if (entries.length === 0) {
    return field === 'status' && all.length > 0 ? (
      <span className="sk:text-fg-tertiary">No carrier updates yet</span>
    ) : undefined;
  }

  const format = (value: string) =>
    field === 'eta'
      ? DateUtility.formatDate(value)
      : formatTrackingStatus(value);

  if (entries.length === 1) {
    return format(entries[0][field] as string);
  }

  return (
    <Tooltip
      content={
        <span className="sk:flex sk:flex-col sk:gap-1">
          {entries.map((entry, index) => (
            <span key={entry._id || index}>
              {entry.carrier?.toUpperCase()} · {entry.totalItems || 0} items ·{' '}
              {format(entry[field] as string)}
            </span>
          ))}
        </span>
      }
    >
      <span className="sk:underline sk:decoration-dotted">Multiple</span>
    </Tooltip>
  );
}

/**
 * The order's own status only turns `Shipped` once every line has, so a partly
 * shipped order still reads as pending. The line states are what reveal it.
 */
function isPartiallyShipped(order: OrderDetail): boolean {
  const states = (order.order_contents || []).map(
    item => item.content?.item_status,
  );

  return (
    states.some(state => state === OrderStatus.Shipped) &&
    states.some(state => PENDING_STATUSES.includes(state || ''))
  );
}

type ProviderOrderRef = {
  id?: string;
  link?: string;
};

/**
 * A manually fulfilled line stores its tracking number where an integration
 * stores a URL, so anything that is not one is a label, not a destination.
 */
function providerLink(value?: string) {
  return value && /^https?:\/\//i.test(value) ? value : undefined;
}

/**
 * MCF numbers the fulfillment per line, and only the line knows which provider
 * order its id belongs to; the order-level pair is a summary that can name a
 * different fulfillment than the link beside it, so it only answers for an
 * order whose lines say nothing.
 */
function providerOrderRefs(order: OrderDetail): ProviderOrderRef[] {
  const lineRefs: ProviderOrderRef[] = [
    ...(order.order_contents || []).map(item => ({
      id: item.content.fulfilment_order_id,
      link: providerLink(item.content.fulfillment_provider_link),
    })),
    ...(order.shipping_details || []).flatMap(pack =>
      (pack.items || []).map(item => ({
        id: item.fulfilment_order_id,
        link: providerLink(item.fulfillment_provider_link),
      })),
    ),
  ];

  const refs = lineRefs.some(ref => ref.id || ref.link)
    ? lineRefs
    : [
        {
          id: order.fulfillment_details?.fulfillment_order_id,
          link: providerLink(
            order.fulfillment_details?.fulfillment_provider_link,
          ),
        },
      ];

  /* The same fulfillment reaches us once per line it carries. */
  const unique = new Map<string, ProviderOrderRef>();

  refs.forEach(ref => {
    const key = ref.id || ref.link;
    if (!key) {
      return;
    }

    const seen = unique.get(key);
    unique.set(key, {
      id: seen?.id || ref.id,
      link: seen?.link || ref.link,
    });
  });

  return [...unique.values()];
}

function fulfillmentIdValue(order: OrderDetail): ReactNode {
  const refs = providerOrderRefs(order);

  if (refs.length === 0) {
    return undefined;
  }

  /* The link is what the order is looked up by, so it is worth showing even
     when the provider gave us no id to print on it. */
  const render = (ref: ProviderOrderRef) =>
    ref.link ? (
      <ExternalLink href={ref.link} className="sk:truncate">
        {ref.id || 'Open provider order'}
      </ExternalLink>
    ) : (
      ref.id
    );

  if (refs.length === 1) {
    return render(refs[0]);
  }

  return (
    <span className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
      {refs.map(ref => (
        <span key={ref.id || ref.link} className="sk:min-w-0 sk:truncate">
          {render(ref)}
        </span>
      ))}
    </span>
  );
}

function createdByValue(order: OrderDetail): ReactNode {
  if (!order.created_by) {
    return undefined;
  }

  return (
    <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5">
      {order.user_image ? (
        <img
          src={order.user_image}
          alt=""
          className="sk:h-4 sk:w-4 sk:shrink-0 sk:rounded-full sk:object-cover"
        />
      ) : null}
      <span className="sk:min-w-0 sk:truncate">{order.created_by}</span>
    </span>
  );
}

export function OrderSummaryCard({
  order,
  tracking,
  isClone,
  className,
}: OrderSummaryCardProps) {
  /* EN-4396: which division the order is in, at the account level only. */
  const { level } = useActiveScope();
  const { isV2 } = useOrdersV2();

  if (!order) {
    return (
      <Card title="Order" className={className}>
        <div className="sk:h-24 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
      </Card>
    );
  }

  const division =
    level === 'account' && !isClone && hasDivision(order)
      ? divisionLabelOf(order)
      : undefined;
  const status = order.summary?.status;
  const fulfillment = order.fulfillment_details;
  /* The label block has its own banner above the cards, not a chip here. */
  const orderErrors = getSummaryCardIssues(order.order_error);
  const partiallyShipped = !isClone && isPartiallyShipped(order);
  /* Read-only information next to the status: the open lines still ship. */
  const partiallyCanceled = isClone ? undefined : order.partially_canceled;
  /* The provider missed its handling deadline; the order itself is unchanged. */
  const pastSla = isClone ? undefined : getPastSla(order);
  const stage = isClone ? null : getStage(order);
  const statusReasons = isClone ? [] : getStatusReasons(order, !!pastSla);
  /* EN-4403: a V2 order names its page state; legacy orders keep the status. */
  const statusLabel =
    !isClone && order?.view_state
      ? VIEW_STATE_LABELS[order.view_state]
      : undefined;
  const held = !isClone && order?.held === true;
  const statusText = status
    ? (statusLabel ?? formatOrderStatus(status))
    : undefined;
  /* EN-4544: where an Unfulfilled V2 order is with its provider, as the list row says it ("Sent to 3PLify"): it
     stands for the status and for the Routed or At provider stage. */
  const providerSend = isClone ? null : getProviderSend(order);
  /* A stage that only repeats the status ("Shipped" twice) is left out. */
  const showStage =
    !!stage &&
    stage.label !== statusText &&
    !(
      providerSend &&
      (order.stage === 'routed' || order.stage === 'at_provider')
    );

  /* Lines, not quantities — and the clone screen edits them as it goes. */
  const totalItems = order.order_contents?.length ?? order.total_items;
  const orderId = order.summary?.marketOrderID || order._id;
  /* Where the order came from is also where its channel-side fulfillment is
     reported, which for FBA is the only place the tracking can be read. */
  const orderLink = isClone ? undefined : order.marketplace_link;
  const orderTotal = CommonUtility.currencyFormat(
    order.sales_proceeds?.grand_total,
    order.currency_code,
  );

  /* The deadline to ship by; the API only fills one of the two. */
  const target = order.handling_time || order.latest_shipped_date;
  const targetDate =
    target && !isClone ? DateUtility.formatDate(target) : undefined;

  const delivery = order.eta || order.latest_delivery_date;
  const deliverBy =
    delivery && !isClone ? DateUtility.formatDate(delivery) : undefined;

  /* Before fulfillment the carrier is only known per package. */
  const carriers =
    fulfillment?.shipping_carrier ||
    [
      ...new Set(
        (order.shipping_details || [])
          .map(pack => pack.shipping_carrier)
          .filter(Boolean),
      ),
    ].join(', ');

  const fields = {
    trackingStatus: trackingValue(tracking, 'status'),
    trackingEta: trackingValue(tracking, 'eta'),
    carrier: status === OrderStatus.Shipped ? undefined : carriers,
    channel: fulfillment?.fulfillment_channel,
    fulfillmentId: fulfillmentIdValue(order),
    createdBy: createdByValue(order),
  };

  const hasFulfillment =
    !isClone && Object.values(fields).some(value => !!value);

  return (
    <Card
      title={
        <span className="sk:flex sk:min-w-0 sk:flex-col sk:gap-2">
          <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-2.5">
            <MarketplaceAvatar
              marketplace={{ type: order.marketplace }}
              fallbackText={order.marketplaceName || order.marketplace}
              shopName={order.shopName || order.marketplaceName}
              size="lg"
            />
            <span className="sk:flex sk:min-w-0 sk:flex-col">
              <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-2.5">
                {orderLink ? (
                  <ExternalLink
                    href={orderLink}
                    className="sk:truncate sk:text-sm sk:font-semibold"
                  >
                    {orderId}
                  </ExternalLink>
                ) : (
                  <span className="sk:truncate sk:text-sm sk:font-semibold sk:text-fg">
                    {orderId}
                  </span>
                )}
                <CopyButton value={orderId} label="Copy order ID" />
              </span>
              <span className="sk:text-xs sk:font-normal sk:text-fg-secondary">
                {totalItems} {totalItems === 1 ? 'item' : 'items'} ·{' '}
                {orderTotal}
                {division ? ` · ${division}` : null}
              </span>
            </span>
          </span>
          {orderErrors.length > 0 ? (
            <OrderIssues errors={orderErrors} className="sk:mt-0" />
          ) : null}
        </span>
      }
      className={className}
      bodyClassName="sk:flex sk:flex-col sk:gap-4"
    >
      <FieldGrid className="sk:grid-cols-1">
        {/* EN-4464: V2 orders carry the key (null before the id existed, a dash); legacy ones leave it off. */}
        <ValueField
          label={SHORT_ID_LABEL}
          value={
            !isClone && order.short_id !== undefined ? (
              <ShortIdCell shortId={order.short_id} />
            ) : undefined
          }
        />
        <ValueField
          label="Status"
          value={
            status ||
            providerSend ||
            partiallyShipped ||
            partiallyCanceled ||
            pastSla ||
            stage ||
            held ||
            statusReasons.length > 0 ? (
              <span className="sk:flex sk:min-w-0 sk:flex-wrap sk:items-center sk:gap-1.5">
                {providerSend ? (
                  <ProviderSendBadge order={order} />
                ) : status ? (
                  <Badge tone={getOrderStatusTone(status)}>{statusText}</Badge>
                ) : null}
                {showStage ? <StageBadge order={order} /> : null}
                <HeldBadge held={held} />
                {partiallyShipped ? (
                  <Tooltip content="Some lines have shipped, the rest are still open.">
                    <Badge tone="info">Partially shipped</Badge>
                  </Tooltip>
                ) : null}
                {partiallyCanceled ? (
                  <PartiallyCanceledBadge cancellation={partiallyCanceled} />
                ) : null}
                <PastSlaBadge pastSla={pastSla} />
                {statusReasons.map(reason => (
                  <StatusReasonBadge
                    key={`${reason.source}:${reason.code}:${reason.request_id ?? ''}`}
                    reason={reason}
                  />
                ))}
              </span>
            ) : undefined
          }
        />
        <ValueField label="Ship By" value={targetDate} />
        <ValueField label="Deliver By" value={deliverBy} />
        {/* The age cell prints the purchase timestamp under the elapsed time,
            so the old Purchase Date field would only repeat it. */}
        <ValueField
          label="Order Age"
          value={
            order.summary?.purchaseDate && !isClone ? (
              <OrderAgeCell
                acknowledgedAt={order.summary.purchaseDate}
                isCounting={!!status && PENDING_STATUSES.includes(status)}
              />
            ) : undefined
          }
        />
      </FieldGrid>

      {hasFulfillment && (
        <>
          <div className="sk:border-t sk:border-rule" />
          <FieldGrid className="sk:grid-cols-1">
            {/* Not on V2: both come from the legacy labels' carrier tracking. */}
            {isV2 ? null : (
              <>
                <ValueField
                  label="Carrier Status"
                  value={fields.trackingStatus}
                />
                <ValueField
                  label="Estimated Completion"
                  value={fields.trackingEta}
                />
              </>
            )}
            <ValueField label="Shipping Carrier" value={fields.carrier} />
            <ValueField label="Fulfillment Channel" value={fields.channel} />
            <ValueField
              label="Provider Order ID"
              value={fields.fulfillmentId}
            />
            <ValueField label="Created By" value={fields.createdBy} />
          </FieldGrid>
        </>
      )}
    </Card>
  );
}
